import {getTestProps} from '@benchling/js-test-utils';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {BulkAssemblyFragmentBin} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {getNameResolver, NameResolutionType} from 'molbio/bulk-assembly/util/NameResolution';
import OptionCellDropdown from 'notebook/plain-tables/selectors/OptionCellDropdown';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';

interface BinOptionProps {
  binName: string;
  color: string;
}

function BinOption({binName, color}: BinOptionProps): JSX.Element {
  return (
    <span>
      <span
        aria-hidden={true}
        className={css(styles.binColor)}
        style={{backgroundColor: color}}
        {...getTestProps({component: 'BulkAssemblyBinSelector', element: 'binColor'})}
      />
      {binName}
    </span>
  );
}

interface BulkAssemblyBinSelectorProps {
  typedValue: TypedValue | null;
  onUpdate: (typedValue: TypedValue) => void;
  bins: Array<BulkAssemblyFragmentBin>;
  locked: boolean;
}

/**
 * Cell selector component for bulk assembly bins.
 *
 * Each bin is rendered with the bin color to its left.
 */
export default function BulkAssemblyBinSelector(props: BulkAssemblyBinSelectorProps): JSX.Element {
  const binResolver = getNameResolver(props.bins, (bin) => bin.name);
  const cellText = props.typedValue ? formatTypedValue(props.typedValue) : '';
  // For now, we always re-parse the bin name here even if it's a valid bin. In the future, ideally we would
  // be able to pass down the binId (when applicable) on the TypedValue.
  const resolvedBin = binResolver(cellText);
  const staticDisplay =
    resolvedBin.type === NameResolutionType.FOUND ? (
      <BinOption binName={resolvedBin.value.name} color={resolvedBin.value.color} />
    ) : (
      cellText
    );

  if (props.locked) {
    // OptionCellDropdown's "locked" gives a dropdown of grayed-out choices. In our case, we
    // want no dropdown at all, but still want to show the bin color, so render as a separate case.
    return <span>{staticDisplay}</span>;
  } else {
    return (
      <OptionCellDropdown
        locked={false}
        isSelected={true}
        options={props.bins.map((bin) => ({
          node: <BinOption binName={bin.name} color={bin.color} />,
          onUpdateKey: bin.name,
        }))}
        onUpdate={(newBinName) => {
          props.onUpdate({type: BaseTypedValueType.STRING, rawValue: newBinName});
        }}
        fullWidth={true}
        lightHoverBackground={true}
        staticDisplay={staticDisplay}
      />
    );
  }
}

const styles = StyleSheet.create({
  binColor: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: 4,
    marginRight: 6,
  },
});
